import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { colors, radii } from '../theme/tokens';
import Icon from './ui/Icon';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const LABELS = {
  code: 'Part number',
  position: 'Position',
  make: 'Make',
  model: 'Model',
  variant: 'Variant',
  from: 'Year from',
  to: 'Year to',
  quantity: 'Quantity (metres)',
  unit: 'Unit',
  clientName: 'Company name',
  place: 'Area',
  industry: 'Industry',
  regionName: 'Region',
  erpCode: 'Customer code',
  status: 'Status',
  title: 'Title',
  departmentName: 'Department',
  attendees: 'Attendees',
  details: 'Message',
  taskTypeName: 'Task type',
  priority: 'Priority',
  startDate: 'Start date',
  deadline: 'Deadline',
  checklistText: 'Checklist'
};

function authHeaders(token) {
  return { Authorization: `Bearer ${token}` };
}

function DraftCard({ draft, token, onChange, onRemove, onNotice }) {
  const [fields, setFields] = useState(draft.fields || {});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setFields(draft.fields || {});
  }, [draft]);

  const set = (key, value) => setFields((prev) => ({ ...prev, [key]: value }));

  const save = async (nextFields, extra = {}) => {
    const response = await axios.patch(
      `${API_URL}/api/voice-drafts/${draft._id}`,
      { fields: nextFields, ...extra },
      { headers: authHeaders(token) }
    );
    onChange(response.data);
    return response.data;
  };

  const saveBlur = () => {
    save(fields).catch((err) => {
      setError(err.response?.data?.error || 'Could not update this note');
    });
  };

  const choose = (action) => {
    setBusy(true);
    setError('');
    save({}, { action })
      .catch((err) => setError(err.response?.data?.error || 'Could not update this note'))
      .finally(() => setBusy(false));
  };

  const push = async () => {
    setBusy(true);
    setError('');
    try {
      const saved = await save(fields);
      if (!saved.canPush) {
        setError(saved.blockedReason || (saved.hints && saved.hints[0]) || 'Fill in the missing fields first.');
        return;
      }
      const response = await axios.post(
        `${API_URL}/api/voice-drafts/${draft._id}/push`,
        { fields: saved.fields },
        { headers: authHeaders(token) }
      );
      onRemove(draft._id);
      onNotice(response.data.message || 'Saved');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API_URL}/api/voice-drafts/${draft._id}`, { headers: authHeaders(token) });
      onRemove(draft._id);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not delete');
      setBusy(false);
    }
  };

  const pickCar = (event) => {
    const option = (fields.carOptions || []).find((item) => item.label === event.target.value);
    if (!option) return;
    const next = { ...fields, variant: option.variant, from: option.from, to: option.to };
    setFields(next);
    save(next).catch((err) => setError(err.response?.data?.error || 'Could not update this note'));
  };

  const inputStyle = {
    width: '100%',
    boxSizing: 'border-box',
    marginTop: 4,
    background: colors.inputBg,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: radii.sm,
    padding: '8px 10px',
    fontSize: 14
  };

  const show = (key) => (
    <label key={key} style={{ display: 'block', marginBottom: 8 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: colors.textMuted }}>{LABELS[key]}</span>
      {key === 'details' || key === 'checklistText' ? (
        <textarea
          value={fields[key] || ''}
          onChange={(event) => set(key, event.target.value)}
          onBlur={saveBlur}
          rows={3}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      ) : (
        <input
          value={fields[key] ?? ''}
          onChange={(event) => set(key, event.target.value)}
          onBlur={saveBlur}
          style={inputStyle}
        />
      )}
    </label>
  );

  return (
    <article
      style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: radii.lg,
        padding: 14,
        marginBottom: 12
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
        <strong style={{ color: colors.parGreenBright }}>{draft.label}</strong>
        {draft.summary ? <span style={{ fontSize: 12, color: colors.textMuted, textAlign: 'right' }}>{draft.summary}</span> : null}
      </div>
      <p style={{ margin: '0 0 10px', fontSize: 13, color: colors.textMuted, lineHeight: 1.4 }}>
        “{draft.transcript}”
      </p>

      {draft.action === 'unknown' && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          {[
            ['log_part', 'Car part'],
            ['new_lead', 'Lead'],
            ['new_task', 'Task']
          ].map(([action, label]) => (
            <button key={action} type="button" onClick={() => choose(action)} disabled={busy} style={choiceStyle}>
              {label}
            </button>
          ))}
        </div>
      )}

      {draft.action === 'log_part' && (
        <>
          {(fields.carOptions || []).length > 1 && (
            <label style={{ display: 'block', marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: colors.textMuted }}>Matching car</span>
              <select
                value={(fields.carOptions || []).find((option) => option.variant === fields.variant && String(option.from) === String(fields.from) && String(option.to) === String(fields.to))?.label || ''}
                onChange={pickCar}
                style={inputStyle}
              >
                <option value="">Choose variant and years</option>
                {fields.carOptions.map((option) => (
                  <option key={option.label} value={option.label}>{option.label}</option>
                ))}
              </select>
            </label>
          )}
          {['make', 'model', 'variant'].map(show)}
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>{show('from')}</div>
            <div style={{ flex: 1 }}>{show('to')}</div>
          </div>
          {['code', 'position', 'quantity'].map(show)}
        </>
      )}
      {draft.action === 'new_lead' && (
        <>
          {['clientName', 'place'].map(show)}
          <p style={noteStyle}>Anything else in the note is saved as the first message.</p>
          {show('details')}
        </>
      )}
      {draft.action === 'new_task' && (
        <>
          {['departmentName', 'attendees'].map(show)}
          <p style={noteStyle}>Anything else in the note is saved as the first message.</p>
          {show('details')}
        </>
      )}

      {(draft.warnings || []).map((warning) => (
        <div key={warning} style={{ fontSize: 12, color: colors.amber, marginBottom: 4 }}>{warning}</div>
      ))}
      {(draft.hints || []).map((hint) => (
        <div key={hint} style={{ fontSize: 12, color: colors.amber, marginBottom: 4 }}>{hint}</div>
      ))}
      {draft.blockedReason ? (
        <div style={{ fontSize: 12, color: colors.red, marginBottom: 4 }}>{draft.blockedReason}</div>
      ) : null}
      {error ? <div style={{ fontSize: 12, color: colors.red, marginBottom: 4 }}>{error}</div> : null}

      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button type="button" onClick={push} disabled={busy} style={pushStyle(!busy)}>
          {busy ? 'Working…' : 'Push'}
        </button>
        <button type="button" onClick={remove} disabled={busy} style={deleteStyle}>
          Delete
        </button>
      </div>
    </article>
  );
}

const TARGETS = [
  ['log_part', 'Car parts', 'Make, model, variant (usually Normal), years, part number, position, quantity.'],
  ['new_lead', 'Leads', 'Company name, then the area.'],
  ['new_task', 'Tasks', 'Department, then who it is for. The rest stays in the message.']
];

const VoiceRecorder = ({ onClose }) => {
  const { token } = useAuth();
  const [drafts, setDrafts] = useState([]);
  const [target, setTarget] = useState('');
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const targetRef = useRef('');
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const discardRef = useRef(false);
  const chunksRef = useRef([]);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/api/voice-drafts`, { headers: authHeaders(token) })
      .then((response) => { if (!cancelled) setDrafts(response.data || []); })
      .catch(() => { if (!cancelled) setError('Could not load saved notes'); });
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, []);

  useEffect(() => {
    if (!recording) return undefined;
    const timer = setInterval(() => setSeconds((value) => value + 1), 1000);
    return () => clearInterval(timer);
  }, [recording]);

  const releaseMic = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const upload = async (blob) => {
    setWorking(true);
    setError('');
    setNotice('');
    try {
      const form = new FormData();
      const name = blob.type && blob.type.includes('mp4') ? 'note.m4a' : 'note.webm';
      form.append('action', targetRef.current);
      form.append('audio', blob, name);
      const response = await axios.post(`${API_URL}/api/voice-drafts`, form, {
        headers: authHeaders(token)
      });
      setDrafts((prev) => [response.data, ...prev.filter((item) => item._id !== response.data._id)]);
    } catch (err) {
      setError(err.response?.data?.error || 'Transcription failed');
    } finally {
      setWorking(false);
    }
  };

  const stopRecording = () => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
    setRecording(false);
  };

  const startRecording = async () => {
    setError('');
    setNotice('');
    if (!targetRef.current) {
      setError('Choose Car parts, Tasks, or Leads first.');
      return;
    }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError('This browser cannot record audio.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const preferred = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']
        .find((type) => window.MediaRecorder && MediaRecorder.isTypeSupported(type));
      const recorder = new MediaRecorder(stream, preferred ? { mimeType: preferred } : undefined);
      chunksRef.current = [];
      discardRef.current = false;
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        releaseMic();
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        chunksRef.current = [];
        if (!discardRef.current && blob.size > 0) upload(blob);
      };
      recorderRef.current = recorder;
      recorder.start();
      setSeconds(0);
      setRecording(true);
    } catch (_) {
      releaseMic();
      setError('Microphone permission is needed to record.');
    }
  };

  useEffect(() => {
    if (!recording || seconds < 60) return undefined;
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
    setRecording(false);
    return undefined;
  }, [recording, seconds]);

  const close = () => {
    discardRef.current = true;
    stopRecording();
    releaseMic();
    onClose();
  };

  const clock = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;

  return (
    <div
      role="dialog"
      aria-label="Voice note"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 4000,
        background: colors.bg,
        color: colors.text,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px 0' }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 18 }}>Voice note</div>
          <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
            Choose where it goes, then record. Nothing is written until you tap Push.
          </div>
        </div>
        <button type="button" onClick={close} aria-label="Close" style={iconButton}>
          <Icon name="close" size={22} />
        </button>
      </header>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '18px 16px 8px' }}>
        <div style={{ display: 'flex', gap: 8, width: '100%', maxWidth: 420, marginBottom: 8 }}>
          {TARGETS.map(([id, label]) => (
            <button
              key={id}
              type="button"
              disabled={recording || working}
              onClick={() => {
                targetRef.current = id;
                setTarget(id);
                setError('');
              }}
              style={{
                ...choiceStyle,
                background: target === id ? colors.parGreen : colors.glass,
                color: target === id ? '#fff' : colors.text,
                borderColor: target === id ? colors.parGreen : colors.glassBorder
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <div style={{ fontSize: 13, color: colors.textMuted, textAlign: 'center', minHeight: 36, maxWidth: 420, marginBottom: 12 }}>
          {TARGETS.find(([id]) => id === target)?.[2] || 'Choose Car parts, Tasks, or Leads, then tap record.'}
        </div>
        <button
          type="button"
          onClick={recording ? stopRecording : startRecording}
          disabled={working}
          aria-pressed={recording}
          aria-label={recording ? 'Stop recording' : 'Start recording'}
          style={{
            width: 96,
            height: 96,
            borderRadius: '50%',
            border: 'none',
            background: recording ? colors.red : (target ? colors.parGreen : colors.glass),
            color: target || recording ? '#fff' : colors.textFaint,
            cursor: working ? 'default' : 'pointer',
            boxShadow: recording ? '0 0 0 10px rgba(229,84,75,0.25)' : '0 10px 24px rgba(0,0,0,0.25)'
          }}
        >
          <Icon name={recording ? 'stop' : 'mic'} size={36} color="#fff" />
        </button>
        <div style={{ marginTop: 10, fontWeight: 700 }}>{working ? 'Transcribing…' : clock}</div>
        <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>
          {recording ? 'Tap to stop. Stops automatically at one minute.' : 'Tap to record.'}
        </div>
        {error ? <div style={{ color: colors.red, fontSize: 13, marginTop: 8, textAlign: 'center' }}>{error}</div> : null}
        {notice ? <div style={{ color: colors.parGreenBright, fontSize: 13, marginTop: 8, textAlign: 'center' }}>{notice}</div> : null}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px 28px' }}>
        <div style={{ fontSize: 13, fontWeight: 800, margin: '8px 0' }}>Ready to review</div>
        {drafts.length === 0 ? (
          <div style={{ fontSize: 13, color: colors.textMuted }}>No notes waiting.</div>
        ) : drafts.map((draft) => (
          <DraftCard
            key={draft._id}
            draft={draft}
            token={token}
            onChange={(next) => setDrafts((prev) => prev.map((item) => (item._id === next._id ? next : item)))}
            onRemove={(id) => setDrafts((prev) => prev.filter((item) => item._id !== id))}
            onNotice={setNotice}
          />
        ))}
      </div>
    </div>
  );
};

const noteStyle = {
  margin: '4px 0 8px',
  fontSize: 12,
  color: colors.textMuted
};

const iconButton = {
  width: 40,
  height: 40,
  borderRadius: '50%',
  border: `1px solid ${colors.glassBorder}`,
  background: colors.glass,
  color: colors.text,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer'
};

const choiceStyle = {
  flex: 1,
  background: colors.glass,
  color: colors.text,
  border: `1px solid ${colors.glassBorder}`,
  borderRadius: radii.pill,
  padding: '8px 10px',
  fontWeight: 700,
  fontSize: 13,
  cursor: 'pointer'
};

const deleteStyle = {
  background: 'transparent',
  color: colors.red,
  border: `1px solid ${colors.red}`,
  borderRadius: radii.pill,
  padding: '8px 16px',
  fontWeight: 700,
  cursor: 'pointer'
};

function pushStyle(enabled) {
  return {
    flex: 1,
    background: enabled ? colors.parGreen : colors.glass,
    color: enabled ? '#fff' : colors.textFaint,
    border: 'none',
    borderRadius: radii.pill,
    padding: '10px 16px',
    fontWeight: 800,
    cursor: enabled ? 'pointer' : 'default'
  };
}

export default VoiceRecorder;
